//dependencies
import React, { ReactNode } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";

import { useInputFlash } from "@performyard/py-components";
//components
import { InputWrapper } from "@performyard/py-components";

//hooks
import { useInputId } from "../../../hooks/useInputId";

import { TextSize } from "../../Text";
//styles
import css from "./InputTextArea.module.scss";

type InputTextAreaProps = {
	/**
	 * The value of the input
	 */
	value: string;
	/**
	 * The placeholder for the input
	 */
	placeholder?: string;
	/**
	 * The label for the input
	 */
	label?: string;
	/**
	 * The size of the label
	 */
	labelSize?: TextSize;
	/**
	 * The weight of the label
	 */
	labelWeight?: "regular" | "semi-bold" | "bold";
	/**
	 * Optional subtitle for the input
	 */
	subtitle?: string | ReactNode;
	/**
	 * The class name to apply to the input
	 */
	className?: string;
	/**
	 * The callback for when the value changes
	 */
	onChange: (value: string) => void;
	/**
	 * The callback for when a key is pressed
	 */
	onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;

	/**
	 * Whether or not the input is disabled
	 */
	disabled?: boolean;

	/**
	 * The id of the input. If not provided, a random id will be generated
	 * by nanoid.
	 * @default undefined
	 */
	testId?: string;
	/**
	 * The minimum and maximum number of rows to display
	 */
	minRows?: number;
	/**
	 * The maximum and maximum number of rows to display
	 */
	maxRows?: number;

	/**
	 * The error message to display
	 */
	errorMessage?: string;

	/**
	 * Whether or not the input is errored
	 */
	errored?: boolean;

	/**
	 * The id of the input
	 */
	id?: string;

	/** Change this number to play a one-time blue→white background flash (e.g. after an applied AI suggestion). */
	flashKey?: number;

	/**
	 * Content rendered to the right of the label
	 */
	labelRight?: ReactNode;
	/**
	 * Accessible name for the textarea. Use when there is no visible `label`
	 * but the field still needs a name for screen readers.
	 */
	ariaLabel?: string;
};

/**
 * A controlled textarea input component
 */
const InputTextArea = ({
	id,
	value,
	disabled,
	placeholder,
	label,
	subtitle = null,
	onChange,
	className,
	onKeyDown,
	testId,
	minRows = 1,
	maxRows,
	errorMessage,
	errored = false,
	flashKey,
	labelRight,
	labelSize,
	labelWeight,
	ariaLabel,
}: InputTextAreaProps) => {
	const inputId = useInputId(id);
	const { flashing, onAnimationEnd } = useInputFlash(flashKey);

	return (
		<InputWrapper
			label={label}
			error={errorMessage}
			id={inputId}
			labelRight={labelRight}
			labelSize={labelSize}
			labelWeight={labelWeight}
		>
			{subtitle}
			<ReactTextareaAutosize
				id={inputId}
				value={value}
				aria-label={ariaLabel}
				placeholder={placeholder}
				onChange={(e) => onChange(e.target.value)}
				className={`${css.input} ${errorMessage || errored ? css.error : ""} ${flashing ? "animate-input-flash" : ""} ${className}`}
				onKeyDown={onKeyDown}
				onAnimationEnd={onAnimationEnd}
				data-testid={testId}
				minRows={minRows}
				maxRows={maxRows}
				disabled={disabled}
			/>
		</InputWrapper>
	);
};

export default InputTextArea;
